import { JWT } from '../configs/JWT'

export interface DownloadOptions {
	fallbackFileName: string
}

/**
 * Converts a string to a URL-safe slug.
 * Replaces umlauts, removes special characters, and uses hyphens as separators.
 */
export const slugify = (text: string): string => {
	return text
		.toLowerCase()
		.replace(/ä/g, 'ae')
		.replace(/ö/g, 'oe')
		.replace(/ü/g, 'ue')
		.replace(/ß/g, 'ss')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
}

/**
 * Downloads a file from a URL with JWT authentication.
 * Extracts filename from Content-Disposition header if available.
 *
 * @param downloadUrl - The URL to download from
 * @param options - Download options including fallback filename
 * @throws Error if the request fails
 */
export const downloadFile = async (
	downloadUrl: string,
	options: DownloadOptions
): Promise<void> => {
	const response = await fetch(downloadUrl, {
		headers: {
			Authorization: `Bearer ${JWT.get()}`,
			'X-Request-Source': 'frontend'
		}
	})

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`)
	}

	// Extract filename from Content-Disposition header
	const disposition = response.headers.get('Content-Disposition')
	let fileName = options.fallbackFileName

	if (disposition) {
		// Try UTF-8 encoded filename first
		let match = disposition.match(/filename\*=UTF-8''([^;]+)/)
		if (match?.[1]) {
			fileName = decodeURIComponent(match[1])
		} else {
			// Fallback to simple filename
			match = disposition.match(/filename="?([^"]+)"?/)
			if (match?.[1]) {
				fileName = match[1]
			}
		}
	}

	// Create blob and trigger download
	const blob = await response.blob()
	const url = window.URL.createObjectURL(blob)
	const a = window.document.createElement('a')
	a.href = url
	a.download = fileName
	window.document.body.appendChild(a)
	a.click()
	a.remove()
	window.URL.revokeObjectURL(url)
}
